const API_BASE = '/api/v1';

export interface ApiErrorBody {
  error: {
    /**
     * One of @nks/shared's ERROR_CODES in practice. Typed as string rather than
     * the union because this crosses the wire: a newer API can emit a code this
     * bundle has never heard of, and the client must render it, not crash.
     */
    code: string;
    message: string;
    details: Record<string, unknown>;
    traceId: string;
  };
}

/**
 * A failure the API had an opinion about.
 *
 * Components branch on `code`, never on the message text, which is exactly why
 * the API returns a fixed enum: a duplicate-customer 409 renders a list of
 * matches with actions, not a red toast.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly traceId: string;
  readonly status: number;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.details = body.error.details;
    this.traceId = body.error.traceId;
  }
}

/**
 * Called when the API says this person must choose their own password first
 * (an administrator set it while they were signed in). The session store
 * registers it; api-client cannot import the store, which imports api-client.
 */
let passwordChangeRequired: (() => void) | null = null;
export function onPasswordChangeRequired(fn: (() => void) | null): void {
  passwordChangeRequired = fn;
}

/** In-memory only: an access token in localStorage is readable by any XSS. */
let accessToken: string | null = null;
let activeBranchUid: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}
export function setActiveBranchUid(uid: string | null): void {
  activeBranchUid = uid;
}
export function getActiveBranchUid(): string | null {
  return activeBranchUid;
}

/**
 * Single-flight refresh.
 *
 * This is the one piece of client plumbing that must be right. The API rotates
 * refresh tokens and revokes the whole family when one is replayed, so ten
 * concurrent 401s firing ten concurrent refreshes would look exactly like token
 * theft and sign the user out. Every 401 awaits the same in-flight promise.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async (): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const body = (await res.json()) as { accessToken: string };
      accessToken = body.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Skip the branch header for routes that are not branch-scoped. */
  withoutBranch?: boolean;
  /**
   * Extra request headers. Exists for `Idempotency-Key` (CLAUDE.md §9): a
   * cashier tapping twice on a flaky connection must not take the customer's
   * money twice, and the key has to reach the server to stop it.
   */
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken !== null) headers['Authorization'] = `Bearer ${accessToken}`;
  if (options.withoutBranch !== true && activeBranchUid !== null) {
    headers['X-Branch-Id'] = activeBranchUid;
  }

  Object.assign(headers, options.headers ?? {});

  return fetch(`${API_BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
  });
}

/**
 * The three endpoints that must never trigger a refresh, named individually.
 *
 * `/auth/refresh` would recurse; `/auth/login` and `/auth/logout` 401 when the
 * credentials are simply wrong, and refreshing cannot help that.
 *
 * This used to be `path.startsWith('/auth/')`, which read as the same rule and
 * was not: it also caught `/auth/me`, the one call whose entire job is to turn
 * a surviving refresh cookie back into a session at startup. The result was
 * that every page reload signed the user out while a perfectly valid cookie sat
 * in the jar. Keep this an explicit list — a prefix will silently swallow the
 * next `/auth/*` route somebody adds.
 */
const NO_REFRESH = new Set(['/auth/refresh', '/auth/login', '/auth/logout']);

/**
 * Performs a request, refreshing once on a 401 and replaying the original.
 *
 * Retries exactly once. A second 401 after a successful refresh means the
 * session is genuinely gone, and looping would just hammer the API.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let res = await send(path, options);

  if (res.status === 401 && !NO_REFRESH.has(path)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) res = await send(path, options);
  }

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('text/csv')) return (await res.text()) as T;

  const body: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    if (isApiErrorBody(body)) {
      if (body.error.code === 'PASSWORD_CHANGE_REQUIRED') passwordChangeRequired?.();
      throw new ApiError(res.status, body);
    }
    throw new ApiError(res.status, {
      error: {
        code: 'INTERNAL_ERROR',
        message: 'The server could not be reached. Check your connection and try again.',
        details: {},
        traceId: 'unknown',
      },
    });
  }

  return body as T;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return typeof value === 'object' && value !== null && 'error' in value && typeof value.error === 'object';
}
